import Phaser from 'phaser';
import { World } from '../ecs/World';
import { EventBus } from '../utils/EventBus';
import {
  RenderSystem,
  InputSystem,
  MovementSystem,
  CameraSystem,
  AISystem,
  CombatSystem,
  HealthBarSystem,
  HealingAuraSystem,
  TamingSystem,
  DeathSystem,
  RevivalSystem,
} from '../ecs/systems';
import { TransformComponent } from '../ecs/components/TransformComponent';
import { SpriteComponent } from '../ecs/components/SpriteComponent';
import { MovementComponent } from '../ecs/components/MovementComponent';
import { HealthComponent } from '../ecs/components/HealthComponent';
import { BeastieComponent } from '../ecs/components/BeastieComponent';
import { AIComponent } from '../ecs/components/AIComponent';
import { CombatComponent } from '../ecs/components/CombatComponent';
import { InventoryComponent } from '../ecs/components/InventoryComponent';
import { TilemapManager } from '../world/TilemapManager';
import { TILE_WIDTH, TILE_HEIGHT, worldToScreen } from '../utils/IsometricUtils';
import { PartyStatusPanel, GameOverOverlay } from '../ui';
import { InventoryPanel } from '../ui/InventoryPanel';
import { WildBeastieSpawner } from '../spawning/WildBeastieSpawner';

/**
 * GameScene - Main gameplay scene
 * Handles the game world, ECS updates, and player interaction
 */
export class GameScene extends Phaser.Scene {
  private world!: World;
  private tilemapManager!: TilemapManager;

  /** Map configuration */
  private mapWidth = 40;
  private mapHeight = 40;
  private mapOffsetX = 0;
  private mapOffsetY = 200;

  /** Whether using Tiled tilemap or procedural fallback */
  private useTilemap = false;

  /** System references */
  private renderSystem!: RenderSystem;
  private cameraSystem!: CameraSystem;
  private movementSystem!: MovementSystem;

  /** Debug display */
  private debugText!: Phaser.GameObjects.Text;

  /** Combat log */
  private combatLogContainer!: Phaser.GameObjects.Container;
  private combatLogMessages: { text: string; color: string }[] = [];
  private maxLogMessages = 50; // Store more messages for scrolling
  private visibleLogMessages = 6; // Show 6 at a time
  private logScrollOffset = 0; // Current scroll position

  /** Player spawn position */
  private playerSpawnX = 10;
  private playerSpawnY = 10;

  /** Debug visualization */
  private debugHealingCircle!: Phaser.GameObjects.Graphics;
  private debugHealingText!: Phaser.GameObjects.Text;
  private showHealingRadius = false;
  private healingRadius = 1; // Should match HealingAuraSystem
  private showCollisionRadius = false;

  /** Party status panel */
  private partyStatusPanel!: PartyStatusPanel;

  /** Wild beastie spawner */
  private wildBeastieSpawner!: WildBeastieSpawner;

  /** Game over overlay */
  private gameOverOverlay!: GameOverOverlay;

  /** Inventory panel */
  private inventoryPanel!: InventoryPanel;

  /** Revival system reference */
  private revivalSystem!: RevivalSystem;

  /** Training dummy tracking */
  private trainingDummyId: string | null = null;
  private trainingDummyCounter = 0;
  private dummyRespawnDelay = 3000; // 3 seconds

  /** Whether game is paused due to game over */
  private isGameOver = false;

  constructor() {
    super({ key: 'GameScene' });
  }

  create(): void {
    // Reset game state for fresh start or restart
    this.isGameOver = false;

    this.cameras.main.fadeIn(500);

    // Calculate map offset to center on screen
    this.mapOffsetX = this.cameras.main.width / 2;
    this.mapOffsetY = 200;

    // Initialize ECS world
    this.world = new World(this);

    // Initialize tilemap manager
    this.tilemapManager = new TilemapManager(this);

    // Try to load Tiled tilemap, fall back to procedural
    this.loadMap();

    // Initialize ECS systems (order matters!)
    this.initializeSystems();

    // Register shrines with RevivalSystem (must be after systems are initialized)
    this.revivalSystem.registerShrinesFromZones(this.tilemapManager.getTriggerZones());

    // Create player entity at spawn point
    this.createPlayerEntity();

    // Create starter beastie
    this.createStarterBeastie();

    // Create training dummy for combat testing
    this.createTrainingDummy();

    // Create visual markers for shrines
    this.createShrineMarkers();

    // Initialize wild beastie spawner
    this.wildBeastieSpawner = new WildBeastieSpawner(this.world);
    if (this.useTilemap) {
      this.wildBeastieSpawner.setSpawnPoints(this.tilemapManager.getSpawnPoints());
    }
    // Add test spawn points for testing without Tiled map spawn points
    this.wildBeastieSpawner.addTestSpawnPoints(this.playerSpawnX, this.playerSpawnY);

    // Set up camera
    this.setupCamera();

    // Create debug UI
    this.createDebugUI();

    // Set up event listeners (must be before PartyStatusPanel to avoid removing its listeners)
    this.setupEventListeners();

    // Create party status panel (after setupEventListeners so its listeners aren't removed)
    this.partyStatusPanel = new PartyStatusPanel(this, this.world);

    // Create game over overlay
    this.gameOverOverlay = new GameOverOverlay(this);

    // Create inventory panel
    this.inventoryPanel = new InventoryPanel(this, this.world);

    // I to toggle inventory
    this.input.keyboard?.on('keydown-I', () => {
      if (!this.isGameOver) {
        this.inventoryPanel.toggle();
      }
    });

    // R to use nearby shrine
    this.input.keyboard?.on('keydown-R', () => {
      if (this.isGameOver) return;

      const nearbyShrine = this.revivalSystem.getNearbyShrine();
      const hasFainted = this.revivalSystem.hasFaintedPartyMembers();

      console.log('R pressed - nearbyShrine:', nearbyShrine, 'hasFainted:', hasFainted);
      this.revivalSystem.debugShrineProximity();

      if (nearbyShrine && hasFainted) {
        const revivedCount = this.revivalSystem.useShrine(nearbyShrine);
        console.log('Revived count:', revivedCount);
        if (revivedCount > 0) {
          this.addCombatLogMessage(`Shrine revived ${revivedCount} beastie(s)!`, '#22c55e');
        }
      } else if (!nearbyShrine) {
        this.addCombatLogMessage('No shrine nearby', '#ff6666');
      } else {
        this.addCombatLogMessage('No fainted party members', '#ffaa00');
      }
    });

    // ESC to close inventory or return to menu
    this.input.keyboard?.on('keydown-ESC', () => {
      if (this.inventoryPanel.getIsVisible()) {
        this.inventoryPanel.hide();
      } else {
        this.scene.start('MainMenuScene');
      }
    });

    // H to toggle healing radius debug
    this.input.keyboard?.on('keydown-H', () => {
      this.showHealingRadius = !this.showHealingRadius;
      if (!this.showHealingRadius) {
        this.debugHealingCircle.clear();
        this.debugHealingText.setVisible(false);
      }
    });

    // B to toggle collision box debug
    this.input.keyboard?.on('keydown-B', () => {
      this.showCollisionRadius = !this.showCollisionRadius;
      this.renderSystem.setShowCollisionRadius(this.showCollisionRadius, 0.15);
    });

    // F to feed nearest wild beastie
    this.input.keyboard?.on('keydown-F', () => {
      console.log('F pressed - looking for wild beasties');
      const wildBeasties = this.world.getEntitiesByTag('wild');
      console.log('Found wild beasties:', wildBeasties.length);

      const nearestWild = this.findNearestWildBeastie();
      console.log('Nearest wild:', nearestWild);

      if (nearestWild) {
        console.log('Emitting feed command for:', nearestWild.id);
        EventBus.emitEvent('player:command', {
          command: 'feed_beastie',
          data: { targetId: nearestWild.id, itemId: 'berry' },
        });
      } else {
        EventBus.emitEvent('ui:notification', {
          message: 'No wild beastie nearby',
          type: 'warning',
        });
      }
    });

    // C to capture nearest wild beastie
    this.input.keyboard?.on('keydown-C', () => {
      const nearestWild = this.findNearestWildBeastie();
      if (nearestWild) {
        EventBus.emitEvent('player:command', {
          command: 'capture_beastie',
          data: { targetId: nearestWild.id },
        });
      } else {
        EventBus.emitEvent('ui:notification', {
          message: 'No wild beastie nearby',
          type: 'warning',
        });
      }
    });
  }

  /**
   * Find the nearest wild beastie to the player
   */
  private findNearestWildBeastie(): { id: string; distance: number } | null {
    const player = this.world.getEntity('player');
    if (!player) return null;

    const playerTransform = player.getComponent<TransformComponent>('transform');
    if (!playerTransform) return null;

    const wildBeasties = this.world.getEntitiesByTag('wild');
    let nearest: { id: string; distance: number } | null = null;

    for (const entity of wildBeasties) {
      const transform = entity.getComponent<TransformComponent>('transform');
      if (!transform) continue;

      const distance = Math.sqrt(
        Math.pow(transform.x - playerTransform.x, 2) +
        Math.pow(transform.y - playerTransform.y, 2)
      );

      if (!nearest || distance < nearest.distance) {
        nearest = { id: entity.id, distance };
      }
    }

    return nearest;
  }

  update(_time: number, delta: number): void {
    // Skip updates if game over
    if (this.isGameOver) return;

    // Update ECS world (processes all systems)
    this.world.update(delta);

    // Update debug display
    this.updateDebugDisplay();

    // Update healing radius visualization
    this.updateHealingRadiusDebug();

    // Update party status panel
    this.partyStatusPanel.update();

    // Update wild beastie spawner
    this.wildBeastieSpawner.update(delta);
  }

  /**
   * Load tilemap - tries Tiled first, falls back to procedural
   */
  private loadMap(): void {
    // Check if tilemap data is available
    const tilemapData = this.cache.tilemap.get('meadowvale_test');

    if (tilemapData) {
      try {
        // Load from Tiled JSON
        this.tilemapManager.loadMap('meadowvale_test', [
          { name: 'terrain', key: 'terrain_tileset' },
        ]);

        const mapSize = this.tilemapManager.getMapSize();
        this.mapWidth = mapSize.width;
        this.mapHeight = mapSize.height;

        // Get player spawn from tilemap
        const playerSpawn = this.tilemapManager.getPlayerSpawn();
        if (playerSpawn) {
          this.playerSpawnX = playerSpawn.x;
          this.playerSpawnY = playerSpawn.y;
        } else {
          // Default to center
          this.playerSpawnX = this.mapWidth / 2;
          this.playerSpawnY = this.mapHeight / 2;
        }

        this.useTilemap = true;
        console.log('Loaded Tiled tilemap successfully');
      } catch (error) {
        console.warn('Failed to load Tiled tilemap, using procedural fallback:', error);
        this.createProceduralMap();
      }
    } else {
      console.log('No tilemap data found, using procedural map');
      this.createProceduralMap();
    }

    // Update map offset for systems
    const offset = this.tilemapManager.getOffset();
    if (this.useTilemap) {
      this.mapOffsetX = offset.x;
      this.mapOffsetY = offset.y;
    }
  }

  /**
   * Create procedural tilemap as fallback
   */
  private createProceduralMap(): void {
    const graphics = this.add.graphics();

    // Set default spawn
    this.playerSpawnX = this.mapWidth / 2;
    this.playerSpawnY = this.mapHeight / 2;

    for (let x = 0; x < this.mapWidth; x++) {
      for (let y = 0; y < this.mapHeight; y++) {
        const screenPos = worldToScreen(x, y);

        // Offset to position on screen
        const px = screenPos.x + this.mapOffsetX;
        const py = screenPos.y + this.mapOffsetY;

        // Determine tile type based on position
        const isEdge =
          x === 0 || y === 0 || x === this.mapWidth - 1 || y === this.mapHeight - 1;
        const isPath =
          (x === Math.floor(this.mapWidth / 2) || y === Math.floor(this.mapHeight / 2)) &&
          !isEdge;

        // Set colors
        let fillColor = 0x3d7a5f; // Grass green
        let strokeColor = 0x2d5a47;

        if (isPath) {
          fillColor = 0xd4a574; // Path tan
          strokeColor = 0xb8956a;
        }
        if (isEdge) {
          fillColor = 0x4a5568; // Obstacle gray
          strokeColor = 0x2d3748;
        }

        // Checkerboard pattern for non-edge tiles
        if (!isEdge && !isPath && (x + y) % 2 === 0) {
          fillColor = 0x48a36e;
        }

        graphics.fillStyle(fillColor, 0.9);
        graphics.lineStyle(1, strokeColor, 0.6);

        // Draw isometric diamond
        graphics.beginPath();
        graphics.moveTo(px, py - TILE_HEIGHT / 2);
        graphics.lineTo(px + TILE_WIDTH / 2, py);
        graphics.lineTo(px, py + TILE_HEIGHT / 2);
        graphics.lineTo(px - TILE_WIDTH / 2, py);
        graphics.closePath();
        graphics.fillPath();
        graphics.strokePath();
      }
    }

    // Set depth below entities
    graphics.setDepth(-1000);
  }

  /**
   * Initialize ECS systems in the correct order
   */
  private initializeSystems(): void {
    // Input system - captures keyboard input
    const inputSystem = new InputSystem();
    this.world.addSystem(inputSystem);

    // AI system - beastie behavior
    const aiSystem = new AISystem();
    this.world.addSystem(aiSystem);

    // Movement system - applies velocity
    this.movementSystem = new MovementSystem();
    // Use tilemap collision if available, otherwise fall back to bounds
    if (this.useTilemap) {
      this.movementSystem.setCollisionCheck((x, y) => this.tilemapManager.isWalkable(x, y));
      this.movementSystem.setTriggerCheck((x, y, entityId) => this.tilemapManager.checkTriggers(x, y, entityId));
    } else {
      this.movementSystem.setMapBounds(1, this.mapWidth - 1, 1, this.mapHeight - 1);
    }
    this.world.addSystem(this.movementSystem);

    // Render system - syncs sprites with transforms
    this.renderSystem = new RenderSystem();
    // Use Phaser's tilemap coordinate system for proper alignment
    if (this.useTilemap) {
      const tilemap = this.tilemapManager.getTilemap();
      const groundLayer = this.tilemapManager.getGroundLayer();
      const collisionLayer = this.tilemapManager.getCollisionLayer();
      this.renderSystem.setTilemap(tilemap, groundLayer, collisionLayer);
    } else {
      this.renderSystem.setMapOffset(this.mapOffsetX, this.mapOffsetY);
    }
    this.world.addSystem(this.renderSystem);

    // Combat system - resolves attacks
    const combatSystem = new CombatSystem();
    this.world.addSystem(combatSystem);

    // Healing aura system - player heals nearby party members
    const healingAuraSystem = new HealingAuraSystem();
    this.world.addSystem(healingAuraSystem);

    // Health bar system - visual health bars
    const healthBarSystem = new HealthBarSystem();
    if (this.useTilemap) {
      const groundLayer = this.tilemapManager.getGroundLayer();
      healthBarSystem.setTilemapLayer(groundLayer || null);
    } else {
      healthBarSystem.setMapOffset(this.mapOffsetX, this.mapOffsetY);
    }
    this.world.addSystem(healthBarSystem);

    // Taming system - handles taming wild beasties
    const tamingSystem = new TamingSystem();
    this.world.addSystem(tamingSystem);

    // Death system - handles entity death states
    const deathSystem = new DeathSystem();
    this.world.addSystem(deathSystem);

    // Revival system - handles reviving fainted beasties
    this.revivalSystem = new RevivalSystem();
    this.world.addSystem(this.revivalSystem);

    // Camera system - follows player
    this.cameraSystem = new CameraSystem();
    if (this.useTilemap) {
      const groundLayer = this.tilemapManager.getGroundLayer();
      this.cameraSystem.setTilemapLayer(groundLayer || null);
    } else {
      this.cameraSystem.setMapOffset(this.mapOffsetX, this.mapOffsetY);
    }
    this.world.addSystem(this.cameraSystem);
  }

  /**
   * Create player entity with all required components
   */
  private createPlayerEntity(): void {
    const player = this.world.createEntity('player');

    player.addTag('player');
    player.addTag('controllable');

    // Position at spawn point
    player.addComponent(
      new TransformComponent({
        x: this.playerSpawnX,
        y: this.playerSpawnY,
        z: 0,
        facing: 2, // Facing down
      })
    );

    player.addComponent(
      new MovementComponent({
        maxSpeed: 5, // World units per second
        acceleration: 25,
        friction: 20,
      })
    );

    player.addComponent(
      new SpriteComponent({
        textureKey: 'player_placeholder',
        currentAnimation: 'idle',
      })
    );

    player.addComponent(
      new HealthComponent({
        max: 100,
        current: 100,
        maxEnergy: 50,
        energy: 50,
      })
    );

    // Add inventory with starting items
    const inventory = new InventoryComponent({ maxSlots: 12 });
    player.addComponent(inventory);

    // Give player starting items for testing
    inventory.addItem('revive_herb', 3);
    inventory.addItem('healing_berry', 5);
    inventory.addItem('beastie_treat', 10);
  }

  /**
   * Create starter Solara beastie that follows player
   */
  private createStarterBeastie(): void {
    const beastie = this.world.createEntity('starter_beastie');

    beastie.addTag('beastie');
    beastie.addTag('party_member');

    // Position slightly offset from player spawn
    beastie.addComponent(
      new TransformComponent({
        x: this.playerSpawnX + 1.5,
        y: this.playerSpawnY + 0.5,
        z: 0,
        facing: 4, // Facing left (towards player)
      })
    );

    beastie.addComponent(
      new MovementComponent({
        maxSpeed: 4.5, // Slightly slower than player
        acceleration: 20,
        friction: 18,
      })
    );

    beastie.addComponent(
      new SpriteComponent({
        textureKey: 'beastie_solara',
        currentAnimation: 'idle',
      })
    );

    beastie.addComponent(
      new HealthComponent({
        max: 50,
        current: 50,
        maxEnergy: 60,
        energy: 60,
      })
    );

    beastie.addComponent(
      new BeastieComponent({
        speciesId: 'solara',
        nickname: 'Sunny',
        level: 5,
        elementTypes: ['solar'],
        loyalty: 75,
        personality: 'bold',
        isWild: false,
        ownerId: 'player',
        baseStats: {
          health: 50,
          attack: 60, // Solara has decent attack (6 on 1-10 scale = 60 base)
          defense: 40,
          speed: 70,
          energy: 60,
        },
      })
    );

    beastie.addComponent(
      new AIComponent({
        state: 'following',
        ownerEntityId: 'player',
        targetingPriority: 'same_as_owner',
        leashRange: 10,
        detectionRange: 8,
        isActive: true,
      })
    );

    beastie.addComponent(
      new CombatComponent({
        attackSpeed: 1, // 1 attack per second
        attackRange: 1.5,
        canInitiateCombat: true,
      })
    );
  }

  /**
   * Create training dummy for combat testing
   */
  private createTrainingDummy(): void {
    // Generate unique ID for each dummy
    this.trainingDummyCounter++;
    const dummyId = `training_dummy_${this.trainingDummyCounter}`;
    this.trainingDummyId = dummyId;

    const dummy = this.world.createEntity(dummyId);

    dummy.addTag('enemy');
    dummy.addTag('training_dummy'); // Tag for identification

    // Position near player spawn
    dummy.addComponent(
      new TransformComponent({
        x: 35,
        y: 35,
        z: 0,
        facing: 4, // Facing left
      })
    );

    dummy.addComponent(
      new SpriteComponent({
        textureKey: 'enemy_placeholder',
        currentAnimation: 'idle',
      })
    );

    dummy.addComponent(
      new HealthComponent({
        max: 100,
        current: 100,
        maxEnergy: 0,
        energy: 0,
      })
    );

    dummy.addComponent(
      new MovementComponent({
        maxSpeed: 3,
        acceleration: 15,
        friction: 15,
      })
    );

    dummy.addComponent(
      new AIComponent({
        state: 'combat',
        targetingPriority: 'nearest',
        detectionRange: 6,
        isActive: true,
      })
    );

    dummy.addComponent(
      new CombatComponent({
        attackSpeed: 1, // 1 attack per second
        attackRange: 1.5,
        baseDamage: 15, // Does 15 damage per hit
        canInitiateCombat: true,
        isHostile: true,
      })
    );

    console.log(`Training dummy (${dummyId}) created at`, this.playerSpawnX + 4, this.playerSpawnY);
  }

  /**
   * Create visual markers for shrine locations
   */
  private createShrineMarkers(): void {
    const triggerZones = this.tilemapManager.getTriggerZones();
    const shrines = triggerZones.filter((zone) => zone.type === 'revival_shrine');

    console.log(`Found ${shrines.length} shrines to mark`);

    for (const shrine of shrines) {
      // shrine.x and shrine.y are world grid coordinates
      // Center the marker on the shrine zone
      const centerX = shrine.x + shrine.width / 2;
      const centerY = shrine.y + shrine.height / 2;

      // Convert world coords to screen coords
      let px: number;
      let py: number;

      if (this.useTilemap) {
        const groundLayer = this.tilemapManager.getGroundLayer();
        if (groundLayer) {
          // Use tileToWorldXY with the world grid coordinates (they map to tile indices)
          const tilePos = groundLayer.tileToWorldXY(centerX, centerY);
          if (tilePos) {
            px = tilePos.x;
            py = tilePos.y;
          } else {
            const screenPos = worldToScreen(centerX, centerY);
            px = screenPos.x + this.mapOffsetX;
            py = screenPos.y + this.mapOffsetY;
          }
        } else {
          const screenPos = worldToScreen(centerX, centerY);
          px = screenPos.x + this.mapOffsetX;
          py = screenPos.y + this.mapOffsetY;
        }
      } else {
        const screenPos = worldToScreen(centerX, centerY);
        px = screenPos.x + this.mapOffsetX;
        py = screenPos.y + this.mapOffsetY;
      }

      // Create shrine visual - a glowing circle with text
      const shrineGraphics = this.add.graphics();

      // Outer glow
      shrineGraphics.fillStyle(0x44ffaa, 0.2);
      shrineGraphics.fillEllipse(px, py, TILE_WIDTH * 2, TILE_HEIGHT * 2);

      // Inner circle
      shrineGraphics.fillStyle(0x22cc88, 0.4);
      shrineGraphics.fillEllipse(px, py, TILE_WIDTH, TILE_HEIGHT);

      // Center marker
      shrineGraphics.fillStyle(0xffffff, 0.8);
      shrineGraphics.fillCircle(px, py, 8);

      shrineGraphics.setDepth(-50); // Below entities but above ground

      // Add label
      const label = this.add.text(px, py - 30, 'SHRINE', {
        fontFamily: 'monospace',
        fontSize: '12px',
        color: '#44ffaa',
        stroke: '#000000',
        strokeThickness: 2,
      }).setOrigin(0.5);
      label.setDepth(-49);

      console.log(`Shrine marker created at world(${shrine.x}-${shrine.x + shrine.width}, ${shrine.y}-${shrine.y + shrine.height}) center -> screen(${px.toFixed(0)}, ${py.toFixed(0)})`);
    }
  }

  /**
   * Set up camera system
   */
  private setupCamera(): void {
    this.cameraSystem.setTarget('player');
    this.cameraSystem.configure({
      lerpFactor: 0.08,
      deadzone: { width: 150, height: 100 },
    });

    // Snap camera to player initially
    this.time.delayedCall(100, () => {
      this.cameraSystem.snapToTarget();
    });
  }

  /**
   * Create debug UI overlay
   */
  private createDebugUI(): void {
    this.debugText = this.add.text(10, 10, '', {
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#4ade80',
      backgroundColor: '#000000aa',
      padding: { x: 8, y: 4 },
    });
    this.debugText.setScrollFactor(0);
    this.debugText.setDepth(1000);

    // Combat log - bottom left
    this.combatLogContainer = this.add.container(10, this.cameras.main.height - 140);
    this.combatLogContainer.setScrollFactor(0);
    this.combatLogContainer.setDepth(1000);

    // Background for combat log
    const logBg = this.add.graphics();
    logBg.fillStyle(0x000000, 0.7);
    logBg.fillRoundedRect(0, 0, 320, 110, 4);
    this.combatLogContainer.add(logBg);

    // Make combat log interactive for scroll
    const logHitArea = this.add.rectangle(160, 55, 320, 110, 0x000000, 0);
    logHitArea.setInteractive();
    this.combatLogContainer.add(logHitArea);

    // Mouse wheel scroll for combat log
    this.input.on('wheel', (_pointer: Phaser.Input.Pointer, _gameObjects: Phaser.GameObjects.GameObject[], _deltaX: number, deltaY: number) => {
      // Check if mouse is over the combat log area
      const pointer = this.input.activePointer;
      const logBounds = this.combatLogContainer.getBounds();
      if (pointer.x >= logBounds.x && pointer.x <= logBounds.x + 320 &&
          pointer.y >= logBounds.y && pointer.y <= logBounds.y + 110) {
        if (deltaY > 0) {
          this.scrollCombatLogDown();
        } else if (deltaY < 0) {
          this.scrollCombatLogUp();
        }
      }
    });

    // Controls help
    const controlsText = this.add.text(
      10,
      this.cameras.main.height - 24,
      'WASD: Move | I: Inventory | R: Shrine | F: Feed | C: Capture | B: Hitbox | ESC: Menu',
      {
        fontFamily: 'monospace',
        fontSize: '12px',
        color: '#666666',
      }
    );
    controlsText.setScrollFactor(0);
    controlsText.setDepth(1000);

    // Debug healing circle graphics
    this.debugHealingCircle = this.add.graphics();
    this.debugHealingCircle.setScrollFactor(0); // Fixed to screen
    this.debugHealingCircle.setDepth(500); // Above ground, below entities

    // Debug healing radius text
    this.debugHealingText = this.add.text(0, 0, '', {
      fontFamily: 'monospace',
      fontSize: '12px',
      color: '#4ade80',
      backgroundColor: '#000000aa',
      padding: { x: 4, y: 2 },
    });
    this.debugHealingText.setScrollFactor(0);
    this.debugHealingText.setDepth(501);
    this.debugHealingText.setVisible(false);
  }

  /**
   * Add a message to the combat log
   */
  private addCombatLogMessage(message: string, color: string = '#ffffff'): void {
    this.combatLogMessages.push({ text: message, color });

    // Keep only recent messages
    if (this.combatLogMessages.length > this.maxLogMessages) {
      this.combatLogMessages.shift();
    }

    // Auto-scroll to bottom when new message added
    this.logScrollOffset = Math.max(0, this.combatLogMessages.length - this.visibleLogMessages);

    this.updateCombatLogDisplay();
  }

  /**
   * Update the combat log display with colored messages
   */
  private updateCombatLogDisplay(): void {
    // Remove old text objects (keep the background at index 0)
    while (this.combatLogContainer.length > 1) {
      this.combatLogContainer.removeAt(1, true);
    }

    // Get visible slice of messages based on scroll offset
    const startIndex = this.logScrollOffset;
    const endIndex = Math.min(startIndex + this.visibleLogMessages, this.combatLogMessages.length);
    const visibleMessages = this.combatLogMessages.slice(startIndex, endIndex);

    // Add new text objects for each visible message
    visibleMessages.forEach((msg, index) => {
      const text = this.add.text(8, 6 + index * 16, msg.text, {
        fontFamily: 'monospace',
        fontSize: '12px',
        color: msg.color,
      });
      this.combatLogContainer.add(text);
    });

    // Add scroll position indicator if there are more messages
    if (this.combatLogMessages.length > this.visibleLogMessages) {
      const totalMessages = this.combatLogMessages.length;
      const maxScroll = totalMessages - this.visibleLogMessages;

      // Show position as "X-Y of Z" format
      const firstShown = this.logScrollOffset + 1;
      const lastShown = Math.min(this.logScrollOffset + this.visibleLogMessages, totalMessages);
      const positionText = this.add.text(
        8,
        96,
        `${firstShown}-${lastShown} of ${totalMessages}`,
        {
          fontFamily: 'monospace',
          fontSize: '9px',
          color: '#666666',
        }
      );
      this.combatLogContainer.add(positionText);

      // Draw a small scrollbar on the right
      const scrollbarHeight = 80;
      const scrollbarX = 308;
      const scrollbarY = 8;

      const scrollbar = this.add.graphics();
      // Background track
      scrollbar.fillStyle(0x333333, 0.5);
      scrollbar.fillRect(scrollbarX, scrollbarY, 4, scrollbarHeight);

      // Thumb position
      const thumbHeight = Math.max(20, (this.visibleLogMessages / totalMessages) * scrollbarHeight);
      const thumbY = scrollbarY + (this.logScrollOffset / maxScroll) * (scrollbarHeight - thumbHeight);
      scrollbar.fillStyle(0x888888, 0.8);
      scrollbar.fillRoundedRect(scrollbarX, thumbY, 4, thumbHeight, 2);

      this.combatLogContainer.add(scrollbar);
    }
  }

  /**
   * Scroll combat log up
   */
  private scrollCombatLogUp(): void {
    if (this.logScrollOffset > 0) {
      this.logScrollOffset--;
      this.updateCombatLogDisplay();
    }
  }

  /**
   * Scroll combat log down
   */
  private scrollCombatLogDown(): void {
    if (this.logScrollOffset < this.combatLogMessages.length - this.visibleLogMessages) {
      this.logScrollOffset++;
      this.updateCombatLogDisplay();
    }
  }

  /**
   * Update debug display with player position
   */
  private updateDebugDisplay(): void {
    const player = this.world.getEntity('player');
    if (!player) return;

    const transform = player.getComponent<TransformComponent>('transform');
    const movement = player.getComponent<MovementComponent>('movement');

    if (!transform || !movement) return;

    const mapType = this.useTilemap ? 'Tiled' : 'Procedural';
    const wildBeasties = this.world.getEntitiesByTag('wild');
    const partyMembers = this.world.getEntitiesByTag('party_member');

    const lines = [
      `Beastie-Master v0.1.0-dev`,
      `Map: ${mapType} (${this.mapWidth}x${this.mapHeight})`,
      `Position: (${transform.x.toFixed(1)}, ${transform.y.toFixed(1)})`,
      `Moving: ${movement.isMoving ? 'Yes' : 'No'}`,
      `Wild: ${wildBeasties.length} | Party: ${partyMembers.length}`,
    ];

    this.debugText.setText(lines.join('\n'));
  }

  /**
   * Update healing radius debug visualization
   */
  private updateHealingRadiusDebug(): void {
    if (!this.showHealingRadius) return;

    const player = this.world.getEntity('player');
    if (!player) return;

    const transform = player.getComponent<TransformComponent>('transform');
    if (!transform) return;

    // Clear previous frame
    this.debugHealingCircle.clear();

    // Get player sprite to find its actual screen position
    const sprite = player.getComponent<SpriteComponent>('sprite');
    if (sprite?.sprite) {
      // Use the sprite's actual position on screen
      const camera = this.cameras.main;
      const screenX = sprite.sprite.x - camera.scrollX;
      const screenY = sprite.sprite.y - camera.scrollY;

      // Convert world radius to screen ellipse dimensions
      // In isometric view, a circle becomes an ellipse
      const radiusX = this.healingRadius * TILE_WIDTH;
      const radiusY = this.healingRadius * TILE_HEIGHT;

      // Draw the ellipse
      this.debugHealingCircle.lineStyle(2, 0x4ade80, 0.8); // Green outline
      this.debugHealingCircle.fillStyle(0x4ade80, 0.15); // Green fill with transparency
      this.debugHealingCircle.fillEllipse(screenX, screenY, radiusX * 2, radiusY * 2);
      this.debugHealingCircle.strokeEllipse(screenX, screenY, radiusX * 2, radiusY * 2);

      // Show radius text above the ellipse
      this.debugHealingText.setText(`Heal radius: ${this.healingRadius} units`);
      this.debugHealingText.setPosition(screenX - this.debugHealingText.width / 2, screenY - radiusY - 20);
      this.debugHealingText.setVisible(true);
    }
  }

  /**
   * Set up event listeners
   */
  private setupEventListeners(): void {
    // Remove specific listeners to prevent duplicates on scene restart
    // (Don't use removeAllListeners as it removes PartyStatusPanel's listeners too)
    EventBus.off('player:moved');
    EventBus.off('beastie:spawned');
    EventBus.off('combat:attack');
    EventBus.off('combat:target_defeated');
    EventBus.off('beastie:healed');
    EventBus.off('taming:started');
    EventBus.off('taming:fed');
    EventBus.off('taming:captured');
    EventBus.off('taming:fled');
    EventBus.off('entity:died');
    EventBus.off('entity:fainted');
    EventBus.off('xp:awarded');
    EventBus.off('game:over');
    EventBus.off('beastie:revived');
    EventBus.off('shrine:nearby');

    EventBus.on('player:moved', (_data: { x: number; y: number }) => {
      // Could update minimap, trigger zone changes, etc.
    });

    EventBus.on('beastie:spawned', (data: { id: string }) => {
      console.log(`Beastie spawned: ${data.id}`);
    });

    // Combat log events
    EventBus.onEvent('combat:attack', (data) => {
      const attackerName = this.getEntityDisplayName(data.attackerId);
      const targetName = this.getEntityDisplayName(data.targetId);
      const hpPercent = Math.floor(data.targetHealthPercent * 100);

      // Color based on who is attacking
      const attacker = this.world.getEntity(data.attackerId);
      const isEnemyAttack = attacker?.hasTag('enemy');
      const color = isEnemyAttack ? '#ff6b6b' : '#fbbf24'; // Red for enemy, yellow/gold for ally

      this.addCombatLogMessage(
        `${attackerName} hits ${targetName} for ${data.damage} dmg (${hpPercent}%)`,
        color
      );
    });

    EventBus.onEvent('combat:target_defeated', (data) => {
      const attackerName = this.getEntityDisplayName(data.attackerId);
      const targetName = this.getEntityDisplayName(data.targetId);
      this.addCombatLogMessage(`${attackerName} defeated ${targetName}!`, '#a855f7'); // Purple for defeat
    });

    EventBus.onEvent('beastie:healed', (data) => {
      const targetName = this.getEntityDisplayName(data.id);
      this.addCombatLogMessage(`Player heals ${targetName} for ${data.amount} HP`, '#4ade80'); // Green for heal
    });

    // Taming events
    EventBus.onEvent('taming:started', (data) => {
      const beastieName = this.getEntityDisplayName(data.beastieId);
      this.addCombatLogMessage(`Started taming ${beastieName}`, '#38bdf8'); // Light blue
    });

    EventBus.onEvent('taming:fed', (data) => {
      const beastieName = this.getEntityDisplayName(data.beastieId);
      const percent = Math.floor((data.newProgress / data.threshold) * 100);
      this.addCombatLogMessage(
        `Fed ${beastieName}: +${data.loyaltyGain} friendship (${percent}%)`,
        '#38bdf8' // Light blue
      );
    });

    EventBus.onEvent('taming:captured', (data) => {
      this.addCombatLogMessage(`Captured ${data.speciesId}! Joined party.`, '#22c55e'); // Bright green
    });

    EventBus.onEvent('taming:fled', (data) => {
      const beastieName = this.getEntityDisplayName(data.beastieId);
      this.addCombatLogMessage(`${beastieName} fled!`, '#f97316'); // Orange
    });

    // Death events
    EventBus.onEvent('entity:died', (data) => {
      const entityName = this.getEntityDisplayName(data.entityId);
      const killerName = this.getEntityDisplayName(data.killedBy);

      if (data.entityType === 'enemy') {
        this.addCombatLogMessage(`${entityName} was defeated by ${killerName}!`, '#a855f7'); // Purple

        // Respawn training dummy after delay
        if (data.entityId === this.trainingDummyId) {
          this.time.delayedCall(this.dummyRespawnDelay, () => {
            if (!this.isGameOver) {
              this.createTrainingDummy();
              this.addCombatLogMessage('A new training dummy appeared!', '#888888');
            }
          });
        }
      } else if (data.entityType === 'party') {
        this.addCombatLogMessage(`${entityName} fainted!`, '#ef4444'); // Red
      } else if (data.entityType === 'player') {
        this.addCombatLogMessage(`You were defeated!`, '#ef4444'); // Red
      }
    });

    EventBus.onEvent('entity:fainted', (data) => {
      const entityName = this.getEntityDisplayName(data.entityId);
      this.addCombatLogMessage(`${entityName} has fainted!`, '#f97316'); // Orange
    });

    EventBus.onEvent('xp:awarded', (data) => {
      const recipientName = this.getEntityDisplayName(data.recipientId);
      this.addCombatLogMessage(`${recipientName} gained ${data.amount} XP!`, '#22d3ee'); // Cyan
    });

    EventBus.onEvent('game:over', () => {
      this.isGameOver = true;
      // Hide inventory panel if open
      if (this.inventoryPanel.getIsVisible()) {
        this.inventoryPanel.hide();
      }
      // GameOverOverlay handles its own show() via event listener
    });

    // Revival events
    EventBus.onEvent('beastie:revived', (data) => {
      const beastieName = this.getEntityDisplayName(data.entityId);
      const percent = Math.floor(data.healthPercent * 100);
      const method = data.revivedBy === 'shrine' ? 'Shrine' : 'Revive Herb';
      this.addCombatLogMessage(`${beastieName} was revived by ${method} (${percent}% HP)!`, '#22c55e');
    });

    EventBus.onEvent('shrine:nearby', (data) => {
      if (data.inRange) {
        this.addCombatLogMessage(`Shrine nearby - press R to revive`, '#38bdf8');
      }
    });
  }

  /**
   * Get display name for an entity
   */
  private getEntityDisplayName(entityId: string): string {
    const entity = this.world.getEntity(entityId);
    if (!entity) return entityId;

    // Check for beastie - use nickname if available, otherwise species
    const beastie = entity.getComponent<BeastieComponent>('beastie');
    if (beastie) {
      if (beastie.nickname) {
        return beastie.nickname;
      }
      // Capitalize species name for wild beasties
      return beastie.speciesId.charAt(0).toUpperCase() + beastie.speciesId.slice(1);
    }

    // Friendly names for known entities
    if (entityId === 'player') return 'Player';
    if (entityId.startsWith('training_dummy')) return 'Dummy';

    return entityId;
  }

  shutdown(): void {
    // Clean up tilemap
    this.tilemapManager.destroy();

    // Clean up party status panel
    this.partyStatusPanel.destroy();

    // Clean up game over overlay
    this.gameOverOverlay.destroy();

    // Clean up inventory panel
    this.inventoryPanel.destroy();

    // Clean up wild beastie spawner
    this.wildBeastieSpawner.destroy();

    // Clean up ECS world
    this.world.destroy();

    // Clean up event listeners
    EventBus.removeAllListeners();
  }
}
